import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum QuoteLineItemKeys {
  lineItemNumber = 'lineItemNumber', // line item number, not to be entered by user
  itemNumber = 'itemNumber', // user entered item number
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  unitOfMeasure = 'unitOfMeasure', // unit of measure for the item
  unitPrice = 'unitPrice', // price per unit
  itemDiscountRate = 'itemDiscountRate', // discount rate for the item
  itemDiscountPrice = 'itemDiscountPrice', // discount price for the item
  isPriceBreak = 'isPriceBreak', // boolean flag to determine if item is a price break
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const quoteOrganizationAddressSchema = yup.object().shape({
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})
export type QuoteOrganizationAddress = yup.InferType<typeof quoteOrganizationAddressSchema>

export const quoteFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: quoteOrganizationAddressSchema.nullable().optional(),
    attn: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  quoteNumber: yup.string().nullable().optional(),
  quoteDate: yup.string().nullable().optional(),
  quoteValidFor: yup.string().nullable().optional(),
  accountNumber: yup.string().nullable().optional(),
  deliveryEstimate: yup.string().nullable().optional(),
  salesperson: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  locale: yup.string().nullable().optional(),
  customer: yup.object().shape({
    billingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        billingAddress: quoteOrganizationAddressSchema.nullable().optional(),
        attn: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  shipTo: yup
    .object()
    .shape({
      companyName: yup.string().nullable().optional(),
      addressLine1: yup.string().nullable().optional(),
      addressLine2: yup.string().nullable().optional(),
      city: yup.string().nullable().optional(),
      state: yup.string().nullable().optional(),
      zipCode: yup.string().nullable().optional(),
      country: yup.string().nullable().optional(),
      phoneNumber: yup.string().nullable().optional(),
      emailAddress: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  items: yup
    .array()
    .of(
      yup.object().shape({
        lineItemNumber: yup.number().nullable().optional(),
        itemNumber: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitOfMeasure: yup.string().nullable().optional(),
        itemDiscountRate: yup.string().nullable().optional(),
        itemDiscountPrice: yup.string().nullable().optional(),
        isPriceBreak: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  notes: yup.string().nullable().optional(),
  footerText: yup.string().nullable().optional(),
})

export type QuoteFileData = yup.InferType<typeof quoteFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<QuoteFileData>

export const quoteFileMappings: RequiredFileData = {
  issuer: {
    companyName: '9e4b1f33-c6d4-4b5a-b6e8-89d7e3f6d2a1',
    address: {
      addressLine1: 'c2a8e5d9-f7b6-4c1d-a9e3-b8d5f2c1a4e7',
      addressLine2: '1f4d7c2e-a9b8-4d3f-b6e5-c2a1f9d8e7b4',
      postalCode: '5e8f2c1d-b7a6-4e9d-c8f3-a2b1e4d7c9f6',
      city: '3d6b9f4e-c8a7-4b2d-e5f1-d9c3a6b8e2f7',
      state: '7a4f2e8d-b1c9-4e6a-f3d5-c7b2a9e4f8d1',
      country: '2b8e4f7d-a9c6-4d1b-e8f3-b5a7c2d9e6f4',
    },
    attn: '6f3d8c1e-b4a7-4f9d-c2e5-a8b6f3d9c1e4',
    phoneNumber: '9c4e7b2f-d8a5-4c1e-b6f3-e9d7c2a8f4b1',
    emailAddress: '1d7f4e8c-a2b9-4d6e-f3c5-b8a4e2d9f7c1',
  },
  quoteNumber: '4b8a2f7e-c9d6-4e1b-a7f3-d5c2b9e6a4f8',
  quoteDate: '8e2d6f4c-b7a9-4c5e-f1d3-e8b4a7c2f9d6',
  quoteValidFor: '3f7d9c2e-a4b8-4f1d-c6e5-b2a7f9d4e8c1',
  accountNumber: '5c8b4f2d-e7a6-4d9c-b1f3-a8e2c7d4f9b6',
  deliveryEstimate: '7d4e2f8c-b9a5-4c1d-e6f3-b8a2c7f4d9e1',
  salesperson: '2f8d4c7e-a6b9-4e1f-d3c5-b7a2f8e4c9d1',
  freightTerms: '9c4f7b2d-e8a5-4d1c-b6f3-c2a8e4d9f7b1',
  currency: '1f8e4d7c-b2a9-4f6e-c3d5-e8b4a7f2c9d6',
  locale: '6d2f8c4e-a7b9-4d1f-e5c3-b8a2f7d4e9c1',
  customer: {
    billingDetails: {
      companyName: '8f4c2d7e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      billingAddress: {
        addressLine1: '2d7f4c8e-a9b6-4d1f-c5e3-b8a2f7d4e9c1',
        addressLine2: '4f8c2d7e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
        postalCode: '7d4f8c2e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
        city: '9c4f7d2e-b8a5-4e1d-c6f3-a8b2c7f4d9e1',
        state: '1f4d7c2e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
        country: '3f7d4c2e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      },
      attn: '5f7d4c2e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      phoneNumber: '7f4d7c2e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      emailAddress: '9f4d7c2e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
    },
  },
  shipTo: {
    companyName: 'a1e3f5d7-c9b4-4f2e-d8a6-b7c9e2f5d8a3',
    addressLine1: 'b2f4d6e8-d0c5-4a3f-e9b7-c8d0f3a6e9b4',
    addressLine2: 'c3a5e7f9-e1d6-4b4a-f0c8-d9e1a4b7f0c5',
    city: 'd4b6f8a0-f2e7-4c5b-a1d9-e0f2b5c8a1d6',
    state: 'e5c7a9b1-a3f8-4d6c-b2e0-f1a3c6d9b2e7',
    zipCode: 'f6d8b0c2-b4a9-4e7d-c3f1-a2b4d7e0c3f8',
    country: 'a7e9c1d3-c5b0-4f8e-d4a2-b3c5e8f1d4a9',
    phoneNumber: 'b8f0d2e4-d6c1-4a9f-e5b3-c4d6f9a2e5b0',
    emailAddress: 'c9a1e3f5-e7d2-4b0a-f6c4-d5e7a0b3f6c1',
  },
  items: [
    {
      lineItemNumber: null,
      itemNumber: '2c4f7d8e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      description: '4c7f4d8e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      unitPrice: '6c4f7d8e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      itemQty: '8c4f7d8e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      unitOfMeasure: 'ac4f7d8e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      itemDiscountRate: 'cc4f7d8e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
      itemDiscountPrice: null,
      isPriceBreak: '7a4b4a76-4f2e-4904-9509-fed98a3a2a5d',
      lineTotal: null,
    },
  ],
  notes: 'fc4f7d8e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
  footerText: '1d4f7d8e-b9a5-4e1d-c6f3-a8b2c7f4d9e1',
}
